import {
  ExtrudeGeometry,
  Path,
  Shape,
  ShapeGeometry,
  BufferGeometry,
  PlaneGeometry,
} from "three";
import { MIRROR_FRAME_DEPTH } from "./mirrorConstants";
import type { ConfiguratorState, FrameShape } from "./types";

export function buildDimensions(shape: FrameShape, sizeInches: number): { w: number; h: number } {
  const inchToUnit = 1 / 12;
  const s = sizeInches * inchToUnit;
  let w = s;
  let h = s;
  if (shape === "rectangle" || shape === "hoval") {
    w = s;
    h = s / 2;
  }
  if (shape === "vrect" || shape === "voval") {
    w = s / 2;
    h = s;
  }
  return { w, h };
}

/**
 * Resolve the mirror aperture dimensions from full configurator state.
 * - Preset mode uses `size` (long side) + `sizePresetShort` (short side).
 *   Both come straight from CPB option labels — no hardcoded aspect ratio.
 * - Custom mode uses width/length in the selected unit.
 * - Shape orientation remains canonical (landscape vs portrait).
 */
export function buildDimensionsFromState(state: Pick<
  ConfiguratorState,
  "shape" | "size" | "sizePresetShort" | "sizeMode" | "sizeUnit" | "sizeCustomWidth" | "sizeCustomLength"
>): { w: number; h: number } {
  const inchToUnit = 1 / 12;

  if (state.sizeMode !== "custom") {
    if (state.shape === "square" || state.shape === "circle") {
      return buildDimensions(state.shape, state.size);
    }
    const longIn = Math.max(1, state.size);
    const shortIn =
      state.sizePresetShort > 0 ? state.sizePresetShort : longIn / 2;
    const longU = longIn * inchToUnit;
    const shortU = shortIn * inchToUnit;
    if (state.shape === "rectangle" || state.shape === "hoval") {
      return { w: longU, h: shortU };
    }
    // vrect / voval — long side is vertical
    return { w: shortU, h: longU };
  }

  const unitToInches = state.sizeUnit === "cm" ? 1 / 2.54 : 1;
  const rawWIn = state.sizeCustomWidth * unitToInches;
  const rawHIn = state.sizeCustomLength * unitToInches;
  const fallback = Math.max(1, state.size);
  const widthIn = Number.isFinite(rawWIn) && rawWIn > 0 ? rawWIn : fallback;
  const heightIn = Number.isFinite(rawHIn) && rawHIn > 0 ? rawHIn : fallback;

  if (state.shape === "square" || state.shape === "circle") {
    const sideIn = Math.max(widthIn, heightIn);
    return buildDimensions(state.shape, sideIn);
  }

  // For non-square shapes, preserve the exact custom width/height pair.
  // CPB width/length are user-entered dimensions, so sorting (max/min) would
  // incorrectly force proportional scaling and lose intended aspect ratio.
  const wUnits = widthIn * inchToUnit;
  const hUnits = heightIn * inchToUnit;

  if (state.shape === "rectangle" || state.shape === "hoval") {
    return { w: wUnits, h: hUnits };
  }
  if (state.shape === "vrect" || state.shape === "voval") {
    return { w: hUnits, h: wUnits };
  }

  return { w: wUnits, h: hUnits };
}

export function isCircleShape(shape: FrameShape): boolean {
  return shape === "circle";
}

/**
 * Stadium / discorectangle: two straight edges + two semicircular caps.
 * Horizontal (`hoval`) → straights on top & bottom, caps on left & right.
 * Vertical (`voval`) → straights on left & right, caps on top & bottom.
 *
 * Visually matches the client's Oval GLBs (`OvalHorizontal.glb` /
 * `OvalVertical.glb`), which export a true stadium silhouette — NOT an
 * ellipse — see the Blender source vertex layout.
 */
export function isStadiumShape(shape: FrameShape): boolean {
  return shape === "hoval" || shape === "voval";
}

export function isCurvedShape(shape: FrameShape): boolean {
  return isCircleShape(shape) || isStadiumShape(shape);
}

/** Frame wall thickness (m) — matches the client GLBs when fitting outer bbox. */
export const FRAME_PROFILE_THICKNESS = 0.08;

/**
 * Curve sampling for `ExtrudeGeometry`. Default Three.js value is 12 — that
 * produces a visibly faceted dodecagonal silhouette on a circular / oval
 * frame. 64 gives a smooth-reading outline at every viewing angle the
 * customer can reach (camera distance is locked by `fitRadius`, so no
 * extreme zoom revealing per-facet detail).
 */
const FRAME_CURVE_SEGMENTS = 64;

/**
 * Trace a closed CCW stadium contour on either a `Shape` or a `Path` (both
 * support the same `moveTo`/`lineTo`/`absarc`/`closePath` API). The cap radius
 * is `min(w, h) / 2`; the straight-edge length is `|w − h|` on the longer axis.
 */
function drawStadiumPath(p: Shape | Path, w: number, h: number): void {
  const r = Math.min(w, h) / 2;
  if (w >= h) {
    const hw = w / 2;
    p.moveTo(-hw + r, -r);
    p.lineTo(hw - r, -r);
    p.absarc(hw - r, 0, r, -Math.PI / 2, Math.PI / 2, false);
    p.lineTo(-hw + r, r);
    p.absarc(-hw + r, 0, r, Math.PI / 2, (Math.PI * 3) / 2, false);
    p.closePath();
  } else {
    const hh = h / 2;
    p.moveTo(r, -hh + r);
    p.lineTo(r, hh - r);
    p.absarc(0, hh - r, r, 0, Math.PI, false);
    p.lineTo(-r, -hh + r);
    p.absarc(0, -hh + r, r, Math.PI, Math.PI * 2, false);
    p.closePath();
  }
}

export function makeFrameGeometry(shape: FrameShape, w: number, h: number): ExtrudeGeometry {
  const thickness = FRAME_PROFILE_THICKNESS;
  const depth = MIRROR_FRAME_DEPTH;
  const outerW = w + thickness * 2;
  const outerH = h + thickness * 2;

  const outer = new Shape();
  const inner = new Path();

  if (isCircleShape(shape)) {
    outer.absellipse(0, 0, outerW / 2, outerH / 2, 0, Math.PI * 2, false, 0);
    inner.absellipse(0, 0, w / 2, h / 2, 0, Math.PI * 2, false, 0);
  } else if (isStadiumShape(shape)) {
    drawStadiumPath(outer, outerW, outerH);
    drawStadiumPath(inner, w, h);
  } else {
    outer.moveTo(-outerW / 2, -outerH / 2);
    outer.lineTo(outerW / 2, -outerH / 2);
    outer.lineTo(outerW / 2, outerH / 2);
    outer.lineTo(-outerW / 2, outerH / 2);
    outer.closePath();
    inner.moveTo(-w / 2, -h / 2);
    inner.lineTo(w / 2, -h / 2);
    inner.lineTo(w / 2, h / 2);
    inner.lineTo(-w / 2, h / 2);
    inner.closePath();
  }

  outer.holes.push(inner);

  const geo = new ExtrudeGeometry(outer, {
    depth,
    bevelEnabled: false,
    curveSegments: FRAME_CURVE_SEGMENTS,
  });
  // Pass aperture (inner hole) dims so the face-classifier has an
  // unambiguous threshold (centroid distance vs hole half-extent +
  // wall thickness) and doesn't depend on per-vertex normals — those
  // get averaged at the rectangle corners and give diagonal directions
  // that misclassify the corner triangles.
  classifyFrameFaces(geo, w, h);
  return geo;
}

/**
 * Split frame faces into two material groups via geometry.groups:
 *   - group 0 (materialIndex 0): outer perimeter side walls + top / bottom
 *     caps. Surfaces that face the viewer / environment.
 *   - group 1 (materialIndex 1): inner perimeter side walls — the gold
 *     surface that faces INTO the mirror cavity. These are the surfaces
 *     responsible for the bright envMap "hot-spot" the user can see at
 *     certain camera angles on the inside of the frame.
 *
 * Classification rule per face:
 *   - face normal lies (mostly) in the XY plane → vertical wall
 *   - face normal points TOWARD the frame center on XY → inner wall
 *   - otherwise → outer / cap
 *
 * Faces are emitted by `ExtrudeGeometry` in two ranges (caps first, then
 * the side walls per contour ring), so per-face groups end up as a
 * handful of contiguous ranges — no expensive index reordering needed.
 * `ProcFrameMesh` consumes this geometry with `material={[outer, inner]}`
 * and assigns each group its own material (the inner one ships with
 * `envMapIntensity = 0` to silence the hot-spot).
 */
/**
 * Tag each triangle of the frame extrusion as either an outer/cap face
 * (materialIndex 0 — full PBR gold) or an inner cavity-facing wall
 * (materialIndex 1 — unlit dim background tone).
 *
 * Method: for every face, take its 3-vertex centroid and the magnitude of
 * its representative XY normal component. A vertical-walled face whose
 * centroid sits within the inner-hole half-extents is on the inner
 * perimeter; anything else (caps + outer perimeter walls) is on the
 * outer side. This is robust on rectangles where the side-wall triangle
 * corners share vertex normals with the top cap (averaged smooth normal
 * is diagonal, not flat) — relying on normal direction alone would
 * misclassify those corner triangles.
 *
 * Tolerance: we expand the inner threshold by ~30 % of the frame
 * thickness so triangles whose centroid lands a hair outside the hole
 * (curve-segment sampling for circles / ovals never lands *exactly* on
 * the hole boundary) still classify correctly.
 */
function classifyFrameFaces(
  geo: ExtrudeGeometry,
  holeW: number,
  holeH: number,
): void {
  const pos = geo.attributes.position;
  const norm = geo.attributes.normal;
  if (!pos || !norm) return;
  /**
   * `ExtrudeGeometry` attributes are expanded (three vertices per
   * triangle, no index buffer). We iterate triangles by reading three
   * consecutive vertex slots and emit `geometry.groups` keyed off the
   * vertex range — the WebGL renderer feeds those ranges to
   * `drawArrays` per material.
   */
  const idx = geo.index;
  const useIndex = idx !== null;
  const triCount = (useIndex ? idx!.count : pos.count) / 3;
  geo.clearGroups();

  /**
   * Inner-hole half-extents on each axis, expanded by half the wall
   * thickness as tolerance for centroids that land slightly outside the
   * inner contour (arc sampling, vertex averaging at corners).
   *
   * Earlier this was a single radius `max(holeW, holeH) / 2 + …`, which
   * over-tightened on non-square shapes AND on the off-axis portions of
   * square inner walls: a square-hole inner-wall triangle near the top
   * of the +X side has centroid (~holeW/2, ~holeH/2*0.9) and a centroid
   * length well above `max/2`, so it failed the threshold and stayed in
   * the metal material — that was exactly where the env-map hot-spots
   * surfaced. Per-axis bbox is the correct geometric test.
   */
  const tol = FRAME_PROFILE_THICKNESS * 0.5;
  const innerHalfW = holeW / 2 + tol;
  const innerHalfH = holeH / 2 + tol;

  let runStart = 0;
  let runMat = -1;
  const emit = (endTri: number) => {
    if (runMat < 0 || endTri <= runStart) return;
    geo.addGroup(runStart * 3, (endTri - runStart) * 3, runMat);
  };

  const vertexIdx = (f: number, k: 0 | 1 | 2): number => {
    if (useIndex) return idx!.getX(f * 3 + k);
    return f * 3 + k;
  };

  for (let f = 0; f < triCount; f++) {
    const a = vertexIdx(f, 0);
    const b = vertexIdx(f, 1);
    const c = vertexIdx(f, 2);
    const cenX = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3;
    const cenY = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
    const nx = (norm.getX(a) + norm.getX(b) + norm.getX(c)) / 3;
    const ny = (norm.getY(a) + norm.getY(b) + norm.getY(c)) / 3;
    const nz = (norm.getZ(a) + norm.getZ(b) + norm.getZ(c)) / 3;
    const flatLen = Math.hypot(nx, ny);
    const totalLen = Math.hypot(flatLen, nz);
    let mat = 0;
    if (totalLen > 1e-9) {
      const verticality = flatLen / totalLen;
      const cenLen = Math.hypot(cenX, cenY);
      // Three-stage classification:
      //   1. Vertical wall — normal lies (mostly) in XY plane.
      //   2. Centroid sits inside the (slightly expanded) inner-hole
      //      bbox — keeps OUTER perimeter walls out.
      //   3. Normal points TOWARD the frame centre — handles stadium
      //      curved corners where the inward normal isn't purely radial.
      if (
        verticality > 0.4 &&
        cenLen > 1e-6 &&
        flatLen > 1e-6 &&
        Math.abs(cenX) <= innerHalfW &&
        Math.abs(cenY) <= innerHalfH
      ) {
        const dotToCenter =
          ((-cenX) * nx + (-cenY) * ny) / (flatLen * cenLen);
        if (dotToCenter > 0.3) mat = 1;
      }
    }
    if (mat !== runMat) {
      emit(f);
      runStart = f;
      runMat = mat;
    }
  }
  emit(triCount);
}

/**
 * 2D closed polygon (already in world XY, centered at aperture center).
 * Used for portal / depth layers / cavity wall / LED contour.
 */
export type AperturePolygon = Array<{ x: number; y: number }>;

/** Number of segments per semicircle for stadium/circle hulls. */
export const APERTURE_ARC_SEGMENTS = 32;

/**
 * Procedurally build the aperture hull for any `FrameShape` at world-unit
 * dimensions `w × h`. Rectangle / square → 4 corners (CCW). Circle → smooth
 * 64-gon ellipse. Stadium → two straight segments + two semicircular caps.
 *
 * Replaces the GLB-derived `apertureHull` extracted via `ringHull2D` — the
 * shape and aspect now come from `state.shape + buildDimensions`, not from a
 * cached scene file.
 */
export function buildApertureHull(
  shape: FrameShape,
  w: number,
  h: number,
): AperturePolygon {
  if (isCircleShape(shape)) {
    const rx = w / 2;
    const ry = h / 2;
    const N = APERTURE_ARC_SEGMENTS * 2;
    const out: AperturePolygon = [];
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      out.push({ x: rx * Math.cos(a), y: ry * Math.sin(a) });
    }
    return out;
  }
  if (isStadiumShape(shape)) {
    const r = Math.min(w, h) / 2;
    const N = APERTURE_ARC_SEGMENTS;
    const out: AperturePolygon = [];
    if (w >= h) {
      const hw = w / 2;
      out.push({ x: -hw + r, y: -r });
      out.push({ x: hw - r, y: -r });
      for (let i = 1; i < N; i++) {
        const a = -Math.PI / 2 + (i / N) * Math.PI;
        out.push({ x: hw - r + r * Math.cos(a), y: r * Math.sin(a) });
      }
      out.push({ x: hw - r, y: r });
      out.push({ x: -hw + r, y: r });
      for (let i = 1; i < N; i++) {
        const a = Math.PI / 2 + (i / N) * Math.PI;
        out.push({ x: -hw + r + r * Math.cos(a), y: r * Math.sin(a) });
      }
    } else {
      const hh = h / 2;
      out.push({ x: r, y: -hh + r });
      out.push({ x: r, y: hh - r });
      for (let i = 1; i < N; i++) {
        const a = (i / N) * Math.PI;
        out.push({ x: r * Math.cos(a), y: hh - r + r * Math.sin(a) });
      }
      out.push({ x: -r, y: hh - r });
      out.push({ x: -r, y: -hh + r });
      for (let i = 1; i < N; i++) {
        const a = Math.PI + (i / N) * Math.PI;
        out.push({ x: r * Math.cos(a), y: -hh + r + r * Math.sin(a) });
      }
    }
    return out;
  }
  // Rectangle / square / vrect — 4 corners (CCW)
  const hw = w / 2;
  const hh = h / 2;
  return [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ];
}

/** LED tube radius for the aperture-side bar. Slightly thinner than before. */
export const LED_TUBE_RADIUS = 0.008;

/** Depth of LED-strip extrusion along Z (m). */
const LED_STRIP_DEPTH_Z = 0.014;

/**
 * Radius (m) of the flat point-LED discs that sit flush against the cavity
 * side walls. The real product is a strip of laser-cut holes on the inner
 * frame walls, so each LED reads as a small filled circle on the wall —
 * not a sphere protruding into the cavity.
 *
 * Slightly larger than `LED_TUBE_RADIUS` so a single disc roughly matches
 * the visual mass of the bar tube at the same arc position.
 */
export const LED_POINT_DISC_RADIUS = 0.02;

/**
 * Inset for point-mode discs (slightly inside aperture edge).
 * Half the LED-strip thickness + a hair of margin.
 */
export const LED_CONTOUR_INSET = 0.008;

const LED_POINT_ARC_SPACING = 0.15;
const LED_POINTS_MIN = 16;
const LED_POINTS_MAX = 140;

/** Bounding box of a polygon (w, h). */
export function polygonBBox(poly: AperturePolygon): { w: number; h: number } {
  if (poly.length === 0) return { w: 0, h: 0 };
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { w: maxX - minX, h: maxY - minY };
}

/** Tessellated filled polygon (front-facing +Z) — used for infinity-layer planes / glass. */
export function makePolygonShapeGeometry(
  poly: AperturePolygon,
  scale = 1,
): BufferGeometry {
  if (poly.length < 3) {
    const { w, h } = polygonBBox(poly);
    return new PlaneGeometry(Math.max(0.02, w * scale), Math.max(0.02, h * scale));
  }
  const shape = new Shape();
  shape.moveTo(poly[0].x * scale, poly[0].y * scale);
  for (let i = 1; i < poly.length; i++) {
    shape.lineTo(poly[i].x * scale, poly[i].y * scale);
  }
  shape.closePath();
  return new ShapeGeometry(shape);
}

/**
 * Miter-inset a convex polygon inward by a uniform distance `d` (in world
 * units, after `scale`).
 *
 * For each vertex `v_i` we compute the inward-pointing angle bisector of the
 * two adjacent edges (`e1 = v_{i-1} → v_i`, `e2 = v_i → v_{i+1}`) and shift
 * `v_i` along that bisector by `d / cos(θ/2)`, where `θ/2` is the half-angle
 * between the two edges' inward normals. That keeps every inset edge exactly
 * `d` away from the corresponding outer edge, producing a similar polygon
 * with sharp miter joints — the inner contour of a uniform-width ring.
 *
 * Assumes CCW winding (which is what `buildApertureHull` produces).
 * `MITER_LIMIT` caps the miter length on very sharp corners so we don't get
 * a spike.
 */
export function insetConvexPolygon(
  poly: AperturePolygon,
  scale: number,
  d: number,
): AperturePolygon {
  const n = poly.length;
  if (n < 3) return [];
  const MITER_LIMIT = 4;
  const scaled: AperturePolygon = poly.map((p) => ({
    x: p.x * scale,
    y: p.y * scale,
  }));
  const out: AperturePolygon = [];
  for (let i = 0; i < n; i++) {
    const prev = scaled[(i - 1 + n) % n];
    const curr = scaled[i];
    const next = scaled[(i + 1) % n];
    const e1x = curr.x - prev.x;
    const e1y = curr.y - prev.y;
    const e2x = next.x - curr.x;
    const e2y = next.y - curr.y;
    const l1 = Math.hypot(e1x, e1y);
    const l2 = Math.hypot(e2x, e2y);
    if (l1 < 1e-9 || l2 < 1e-9) {
      out.push(curr);
      continue;
    }
    const u1x = e1x / l1, u1y = e1y / l1;
    const u2x = e2x / l2, u2y = e2y / l2;
    // Inward normal for a CCW polygon = edge direction rotated +90° → (−dy, dx)
    const n1x = -u1y, n1y = u1x;
    const n2x = -u2y, n2y = u2x;
    const bx = n1x + n2x;
    const by = n1y + n2y;
    const bl = Math.hypot(bx, by);
    if (bl < 1e-9) {
      // 180° reversal — degenerate, just offset along n1
      out.push({ x: curr.x + n1x * d, y: curr.y + n1y * d });
      continue;
    }
    const ubx = bx / bl, uby = by / bl;
    const cosHalf = ubx * n1x + uby * n1y;
    const miterLen = Math.min(d / Math.max(0.1, cosHalf), d * MITER_LIMIT);
    out.push({
      x: curr.x + ubx * miterLen,
      y: curr.y + uby * miterLen,
    });
  }
  return out;
}

/**
 * Side-lighting LED bar as a **single** extruded annular ring — outer contour
 * is the aperture hull (scaled), inner contour is that same polygon inset by
 * the strip width. One connected mesh means:
 *
 *   - no visible seams between edge segments;
 *   - no T-joints at rectangle corners;
 *   - one draw call, one alpha value, clean fade with the rest of the stack.
 */
export function makePolygonLedTubes(
  poly: AperturePolygon,
  scale: number,
  pathZ: number,
  radius = LED_TUBE_RADIUS,
): BufferGeometry[] {
  const n = poly.length;
  if (n < 3) return [];

  const scaled: AperturePolygon = poly.map((p) => ({
    x: p.x * scale,
    y: p.y * scale,
  }));
  const outer = new Shape();
  outer.moveTo(scaled[0].x, scaled[0].y);
  for (let i = 1; i < scaled.length; i++) {
    outer.lineTo(scaled[i].x, scaled[i].y);
  }
  outer.closePath();

  // Inner hole inset by `2 × radius` — matches the visual thickness of a
  // circular-section tube (diameter = 2 × radius).
  const stripWidth = radius * 2;
  const inner = insetConvexPolygon(poly, scale, stripWidth);
  if (inner.length >= 3) {
    const hole = new Path();
    hole.moveTo(inner[0].x, inner[0].y);
    for (let i = 1; i < inner.length; i++) {
      hole.lineTo(inner[i].x, inner[i].y);
    }
    hole.closePath();
    outer.holes.push(hole);
  }

  const geo = new ExtrudeGeometry(outer, {
    depth: LED_STRIP_DEPTH_Z,
    bevelEnabled: false,
  });
  // Centre the extrusion depth on pathZ.
  geo.translate(0, 0, pathZ - LED_STRIP_DEPTH_Z / 2);
  return [geo];
}

/** LED point count from scaled polygon perimeter, clamped to [min, max]. */
export function polygonLedPointCount(
  poly: AperturePolygon,
  scale: number,
): number {
  const n = poly.length;
  if (n < 2) return LED_POINTS_MIN;
  let perimeter = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    perimeter += Math.hypot((b.x - a.x) * scale, (b.y - a.y) * scale);
  }
  const k = Math.round(perimeter / LED_POINT_ARC_SPACING);
  return Math.min(LED_POINTS_MAX, Math.max(LED_POINTS_MIN, k));
}

/** A perimeter-sampled LED point with inward edge normal (`nx`, `ny`). */
export type LedPointSample = { x: number; y: number; nx: number; ny: number };

/** Evenly distribute `count` points along the polygon perimeter (by arc length). */
export function polygonLedPointPositions(
  poly: AperturePolygon,
  scale: number,
  layerScale: number,
  count: number,
): LedPointSample[] {
  const n = poly.length;
  if (n < 2 || count <= 0) return [];
  const s = scale * layerScale;
  const pts = poly.map((p) => ({ x: p.x * s, y: p.y * s }));
  const lens: number[] = [];
  const normals: { nx: number; ny: number }[] = [];
  let P = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l = Math.hypot(dx, dy);
    lens.push(l);
    // Inward normal for a CCW polygon = +90° rotation of the edge tangent.
    if (l < 1e-12) {
      normals.push({ nx: 0, ny: 0 });
    } else {
      normals.push({ nx: -dy / l, ny: dx / l });
    }
    P += l;
  }
  if (P < 1e-9) return [];

  /**
   * Sharp-cornered polygons (rectangle / square / vrect — 4 vertices)
   * distribute points **per edge** so opposite sides mirror each other and
   * no light lands in or near a corner: each edge gets `round(count * Lₑ/P)`
   * points placed at `t = (j + 0.5) / k`, which guarantees equal half-slot
   * margin from both endpoints.
   *
   * The arc-length walk below is kept for densely-sampled curves (circle
   * has 64 vertices, stadium ovals 66) where there are no sharp corners and
   * uniform arc-length stepping already reads smooth.
   */
  if (n === 4) return distributePointsPerEdge(pts, lens, normals, P, count);

  const out: LedPointSample[] = [];
  for (let i = 0; i < count; i++) {
    let d = ((i + 0.5) / count) * P;
    for (let e = 0; e < n; e++) {
      if (d <= lens[e] + 1e-9) {
        const t = lens[e] < 1e-12 ? 0 : d / lens[e];
        const a = pts[e];
        const b = pts[(e + 1) % n];
        const { nx, ny } = normals[e];
        out.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y), nx, ny });
        break;
      }
      d -= lens[e];
    }
  }
  return out;
}

function distributePointsPerEdge(
  pts: { x: number; y: number }[],
  lens: number[],
  normals: { nx: number; ny: number }[],
  perimeter: number,
  count: number,
): LedPointSample[] {
  const n = lens.length;
  const raw = lens.map((l) => (l / perimeter) * count);
  const counts = raw.map((r) => Math.round(r));
  // Largest-remainder correction: rounding can drift the sum away from the
  // target `count`; redistribute the delta to the edges with the biggest
  // unmet fractional demand (or surplus) until the sum matches.
  let sum = counts.reduce((a, b) => a + b, 0);
  let safety = n * 4;
  while (sum !== count && safety-- > 0) {
    const residual = raw.map((r, i) => r - counts[i]);
    if (sum < count) {
      let bestI = 0;
      for (let i = 1; i < n; i++) if (residual[i] > residual[bestI]) bestI = i;
      counts[bestI]++;
      sum++;
    } else {
      let bestI = -1;
      for (let i = 0; i < n; i++) {
        if (counts[i] === 0) continue;
        if (bestI < 0 || residual[i] < residual[bestI]) bestI = i;
      }
      if (bestI < 0) break;
      counts[bestI]--;
      sum--;
    }
  }

  const out: LedPointSample[] = [];
  for (let i = 0; i < n; i++) {
    const k = counts[i];
    if (k <= 0) continue;
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const { nx, ny } = normals[i];
    for (let j = 0; j < k; j++) {
      const t = (j + 0.5) / k;
      out.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y), nx, ny });
    }
  }
  return out;
}

/** Export internal knobs so polygon-utilities stay consistent with shape-based ones. */
export const LED_POINTS = {
  MIN: LED_POINTS_MIN,
  MAX: LED_POINTS_MAX,
  SPACING: LED_POINT_ARC_SPACING,
};
